import { useChannelTalk } from '@repo/channel-talk';
import { getCookieData } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { LottiePlayer, useDialog } from '@repo/ui';
import { Button, useToast } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';

import { StatusManualSectionQuery } from '@/__generated__/StatusManualSectionQuery.graphql';
import { revokeCancelMutation } from '@/__generated__/revokeCancelMutation.graphql';
import { tritxRevokeCancelMutation } from '@/__generated__/tritxRevokeCancelMutation.graphql';
import { SummaryModalName, useSummaryModal } from '@/components/tax-refund/common/context/SummaryModalProvider';
import { setIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY } from '@/components/tax-refund/common/data/result-refund-data';
import { revokeCancel } from '@/graphql/mutation/taxRefund/revokeCancel';
import { tritxRevokeCancel } from '@/graphql/mutation/taxRefund/tritxRevokeCancel';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { COMMON_PATHS, DECISION_PATHS, SURVEY_PAGES } from '@/lib/constants/paths';
import { CurrentItemProps, TAX_REFUND_SUMMARY_PAGE_CATEGORY } from '@/lib/constants/summary';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import useDetectOs from '@/lib/hooks/use-detect-os';
import { checkHometaxAvailableTime } from '@/lib/utils/hometax-time';
import { zCurrency } from '@/lib/utils/zCurrency';

export const reviewTasksQuery = graphql`
  query StatusManualSectionQuery($refundHistoryId: String!) {
    getSurveyStatus(refundHistoryId: $refundHistoryId) {
      errors {
        code
      }
    }
    isCertNeed {
      result
    }
    paymentCards {
      cards {
        id
        cardNo
        cardNm
        cardCd
        expireYear
        expireMonth
      }
    }
  }
`;

export const StatusManualSection = ({ item, sectionType }: Pick<CurrentItemProps, 'item' | 'sectionType'>) => {
  const reviewTasks = useLazyLoadQuery<StatusManualSectionQuery>(
    reviewTasksQuery,
    { refundHistoryId: item?.refundHistoryId || '' },
    { fetchPolicy: 'network-only' }
  );
  const { openModal } = useSummaryModal();
  const { openChannelTalk } = useChannelTalk();
  const toast = useToast();
  const { showDialog } = useDialog();
  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const { commit: saveActionLog } = useSaveRefundActionLog();
  const [handleRevokeCancel] = useMutation<revokeCancelMutation>(revokeCancel);
  const [handleTritxRevokeCancel] = useMutation<tritxRevokeCancelMutation>(tritxRevokeCancel);
  const router = useRouter();
  const detectOS = useDetectOs();
  const isIndis = sectionType.startsWith('indis');
  const businessType = isIndis ? 'indis' : 'corps';
  const hasRegisteredCard = reviewTasks?.paymentCards?.cards && reviewTasks.paymentCards.cards.length > 0;

  const handleRevokeCancelCompleted = (result: boolean) => {
    if (result) {
      sendViewEvent('home_toast_retry-success');
      toast({
        content: '이제 환급이 다시 진행돼요.',
        icon: 'check-line',
        iconColor: 'green.600'
      });
      setTimeout(() => {
        router.reload();
      }, 3000);
    } else {
      sendViewEvent('home_toast_retry-fail');
      toast({
        content: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.',
        icon: 'error',
        iconColor: 'red.600'
      });
    }
  };

  const handleRevokeCancelError = (error: unknown) => {
    console.log(error);
    sendViewEvent('home_toast_retry-fail');
    toast({
      content: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.',
      icon: 'error',
      iconColor: 'red.600'
    });
  };

  const ruralSpecialTaxTextButton =
    item?.refundSubStage === 'DeclareSpecialFeeA' || item?.refundSubStage === 'DeclareSpecialFeeB'
      ? {
          textButtonLabel: '농어촌특별세 고지서를 받았다면 확인해주세요',
          textButtonAction: () => {
            sendClickEvent('home_body_rural-guide', { business: businessType });
            openModal(item?.refundSubStage === 'DeclareSpecialFeeA' ? SummaryModalName.RuralSpecialTaxNoPayModal : SummaryModalName.RuralSpecialTaxPayModal, {
              category: TAX_REFUND_SUMMARY_PAGE_CATEGORY
            });
          }
        }
      : {};

  const statusManualContents = (() => {
    let printItem:
      | {
          progressStepIndex?: number;
          iconFileName?: string;
          title?: string;
          description?: ReactNode;
          primaryButtonLabel?: string;
          primaryButtonAction?: () => void;
          basicButtonLabel?: string;
          basicButtonAction?: () => void;
          textButtonLabel?: string;
          textButtonAction?: () => void;
        }
      | undefined = undefined;
    switch (item?.refundStatus) {
      case 'ApplyComplete':
        if (item?.cancelStage === 'Canceling') {
          printItem = {
            iconFileName: 'ani_document-clock.json',
            title: '환급 취소 신청이 접수됐어요',
            description: '영업일 기준 3일 안에 취소가 완료될 예정이에요.',
            basicButtonLabel: '다시 환급 진행하기',
            basicButtonAction: () => {
              sendClickEvent(
                'home_body_refund-again',
                router.query.type === 'indis-tritx' ? { type: 'capital-gain' } : router.query.type === 'indis' ? { type: 'global-income' } : {}
              );
              sendViewEvent('home_dialog_retry');
              showDialog({
                title: '환급을 다시 진행할까요?',
                description: '재조회 없이 진행했던 단계부터 다시 환급 진행이 가능해요.',
                buttons: {
                  primary: {
                    text: '재진행',
                    onClick: () => {
                      sendClickEvent('home_dialog_retry-confirm');
                      if (sectionType === 'indis-tritx') {
                        handleTritxRevokeCancel({
                          variables: { refundId: item.refundId },
                          onCompleted: (response: { tritxRevokeCancel: { result: any } }) => {
                            handleRevokeCancelCompleted(response.tritxRevokeCancel.result);
                          },
                          onError: handleRevokeCancelError
                        } as never);
                      } else {
                        handleRevokeCancel({
                          variables: { refundId: item.refundId },
                          onCompleted: (response: { revokeCancel: { result: any } }) => {
                            handleRevokeCancelCompleted(response.revokeCancel.result);
                          },
                          onError: handleRevokeCancelError
                        } as never);
                      }
                    }
                  },
                  secondary: {
                    text: '취소',
                    onClick: () => {
                      sendClickEvent('home_dialog_retry-cancel');
                    }
                  }
                }
              });
            }
          };
          break;
        }
        if (sectionType !== 'indis-tritx') {
          switch (item?.refundStage) {
            case 'Review':
              switch (item?.refundSubStage) {
                default:
                case 'Default':
                  if (
                    isIndis &&
                    // 설문 가능한 상태 => errors null, 설문 가능하나 기간 만료이면 errors.code === ERR_SURVEY_EXPIRED
                    !['ERR_SURVEY_COMPLETED', 'ERR_ETC'].includes(reviewTasks?.getSurveyStatus?.errors?.code || '')
                  ) {
                    printItem = {
                      title: '빠르게 검토를 마치려면\n사장님의 답변이 필요해요',
                      description: '답변 완료 시, 환급 일정이 평균 7일 빨라져요.',
                      progressStepIndex: 0,
                      primaryButtonLabel: '답변 시작하기',
                      primaryButtonAction: () => {
                        sendClickEvent('home_body_survey-start');
                        router.push(SURVEY_PAGES.설문시작);
                      }
                    };
                    sendViewEvent('home_body_status-review', { status: 'survey-incomplete', business: businessType });
                    break;
                  }
                  if (isIndis && reviewTasks?.isCertNeed?.result) {
                    printItem = {
                      title: '마지막으로 인적공제 환급 받으려면\n홈택스 간편인증이 필요해요',
                      description: '본인과 부양가족에 대한 환급액도 놓치지 마세요.',
                      progressStepIndex: 0,
                      primaryButtonLabel: '간편인증하고 인적공제 받기',
                      primaryButtonAction: () => {
                        sendClickEvent('home_body_personal-deduction-cert');
                        router.push(SURVEY_PAGES.인적공제_간편인증필요);
                      }
                    };
                    sendViewEvent('home_body_status-review', { status: 'personal-deduction-incomplete', business: businessType });
                    break;
                  }
                  printItem = {
                    title: '제휴 세무사가 빠르게 검토해드릴게요',
                    description: '검토 완료 후 알림톡으로 다시 안내드릴게요.',
                    progressStepIndex: 0
                  };
                  sendViewEvent('home_body_status-review', { status: 'start', business: businessType });
                  break;
                case 'ReviewCs':
                  printItem = {
                    title: '확인해주실 부분을 발견했어요',
                    description: '제휴 회계법인에서 02-6080-2162로 전화드릴게요.',
                    progressStepIndex: 0
                  };
                  break;
                case 'ReviewWritingComplete':
                  printItem = {
                    title: '제휴 세무사가 검토를 완료했어요',
                    description: '2일 안에 국세청에서 신고를 완료할 예정이에요.',
                    progressStepIndex: 0
                  };
                  break;
              }
              break;
            case 'Declare':
              printItem = {
                iconFileName: 'ani_alarm-dot.json',
                title: isIndis ? '환급이 결정됐는지 조회해보세요' : '국세청에서 신고서를 검토하고 있어요',
                description: isIndis
                  ? '신고 후 국세청 환급 결정은 평균 2개월 소요되지만,\n빠르면 1개월 이전에도 환급 결정이 될 수 있어요.'
                  : `${item.office ?? ''} 세무서의 환급 결정까지는 평균 2개월 소요돼요.`,
                progressStepIndex: 1,
                primaryButtonLabel: isIndis ? '검토 결과 조회하기' : undefined,
                primaryButtonAction: isIndis
                  ? () => {
                      if (!checkHometaxAvailableTime()) {
                        sendViewEvent('home_dialog_time-block');
                        showDialog({
                          title: '아침 6시 이후에 조회해주세요',
                          description: '매일 밤 0시부터 아침 6시까지 홈택스 점검으로 환급액 조회가 어려워요.',
                          buttons: {
                            primary: {
                              onClick: () => {
                                sendClickEvent('home_dialog_time-block-confirm');
                              }
                            }
                          }
                        });
                        return;
                      }
                      sendClickEvent('home_body_status-declare', { business: businessType });
                      setIndisSearchRefundData({
                        bizName: item?.businessInfo?.taxpayerName ?? '',
                        refundId: item.refundId,
                        refundStatus: item.refundStatus,
                        refundAmount: item.refundAmount,
                        tin: item.tin
                      });
                      const actionCode = router.query.actionCode ?? getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);
                      saveActionLog({
                        variables: {
                          actionCode,
                          refundId: item.refundId,
                          actionStatus: '로그인'
                        }
                      });
                      if (detectOS === 'windows') {
                        router.push({
                          pathname: DECISION_PATHS.결정확인방법선택,
                          query: router.query
                        });
                      } else {
                        router.push({
                          pathname: COMMON_PATHS.간편인증_정보입력,
                          query: Object.assign({}, router.query, { from: 'decision' })
                        });
                      }
                    }
                  : undefined,
                ...ruralSpecialTaxTextButton
              };
              break;
            case 'RefundPreDetermine':
              printItem = {
                title: '국세청 검토 결과, 환급 결정됐어요!',
                description: '결정된 환급액은 영업일 2일 안에 알림톡으로 안내드릴게요.',
                progressStepIndex: 2,
                ...ruralSpecialTaxTextButton
              };
              break;
            case 'RefundDetermine':
              printItem = {
                iconFileName: 'ani_account-arrow-left.json',
                title: `${item.office ?? ''} 세무서에서 곧 입금할 예정이에요!`,
                description: `${item.refundResultDt ? format(item.refundResultDt, 'M월 d일로부터 ') : ''}약 3일 뒤 입금이 예상돼요.`,
                progressStepIndex: 2,
                basicButtonLabel: '환급금 입금 방법 확인',
                basicButtonAction: () => {
                  sendClickEvent('home_body_deposit-guide', { business: businessType });
                  router.push({
                    pathname: COMMON_PATHS.환급금입금방법,
                    query: router.query
                  });
                }
              };
              break;
            case 'RefundCompleteA':
              if (!hasRegisteredCard) {
                printItem = {
                  primaryButtonLabel: '결제하기',
                  primaryButtonAction: () => {
                    sendClickEvent('home_body_payment-start', { business: businessType });
                    if (item?.virtualAccountInfo && item?.userFee) {
                      openModal(SummaryModalName.PaymentAccountModal, {
                        category: TAX_REFUND_SUMMARY_PAGE_CATEGORY,
                        businessType,
                        virtualAccountInfo: item.virtualAccountInfo
                      });
                    } else {
                      toast({
                        icon: 'error',
                        content: '이용료를 확인 중이에요. 카카오톡 알림톡으로 결제를 안내해드릴게요.'
                      });
                    }
                  }
                };
              }
            // eslint-disable-next-line no-fallthrough
            case 'RefundCompleteB':
              printItem = {
                ...printItem,
                iconFileName: 'ani_cash-withdraw.json',
                title: '환급금 잘 받아보셨나요?',
                description:
                  hasRegisteredCard && isIndis ? (
                    <>
                      이용료 {zCurrency(item.userFee, '원', true, '0')}이 {item.paymentExpectDt && format(item.paymentExpectDt, 'M월 d일')}부터 결제 시도돼요.
                    </>
                  ) : (
                    <>
                      이제 제휴 세무 이용료 결제만 남았어요.
                      <br />
                      <strong>{zCurrency(item.userFee, '원', true, '0')}</strong>을 {item.paymentClosingDt && format(item.paymentClosingDt, 'M월 d일')}까지
                      입금해주세요.
                    </>
                  ),
                progressStepIndex: isIndis ? 3 : 2,
                basicButtonLabel: hasRegisteredCard && isIndis ? '결제 수단 보기' : '결제 문의',
                basicButtonAction: () => {
                  if (hasRegisteredCard && isIndis) {
                    router.push(COMMON_PATHS.환급결제카드등록);
                  } else {
                    sendClickEvent('home_body_payment-cx', {
                      status: item.refundStage === 'RefundCompleteA' ? 'pay-available' : 'pay-unavailable',
                      business: businessType
                    });
                    openChannelTalk(SUPPORT_BOT_LINK.신청결과.환급완료);
                  }
                }
              };
              break;
            default:
              printItem = {
                iconFileName: 'ani_check.json',
                title: `${item.applicationDt && format(item.applicationDt, 'yy년 M월 d일,')} 환급 신청을 완료했어요!`,
                description: '환급 업무가 시작되면 알림톡으로 안내드릴게요.'
              };
              break;
          }
        } else {
          printItem = {
            title: `${item.applicationDt && format(item.applicationDt, 'yy년 M월 d일,')} 환급 신청을 완료했어요!`,
            description: '진행 과정은 알림톡으로 별도 안내드릴게요.'
          };
        }
        break;
      case 'RefundImpossible': {
        const lostReason = item.lostReason || '';
        printItem = {
          iconFileName: 'ani_warning-c.json',
          title: '아쉽게도 이번 환급은 어려워요...',
          description: `${lostReason ? lostReason + '\n' : ''}다음번 환급 신청이 가능할 때 알려드릴게요.`
        };
        break;
      }
      case 'RefundFinish':
        printItem = {
          iconFileName: 'ani_heart-c.json',
          title: '비즈넵 환급을 이용해주셔서 감사합니다.',
          description: '다음번 환급도 놓치시지 않도록 알려드릴게요.',
          progressStepIndex: isIndis ? 4 : 3
        };
        break;
      default:
        return undefined;
    }

    return printItem;
  })();

  return (
    <>
      {typeof statusManualContents?.progressStepIndex !== 'undefined' && (
        <ProgressStepper item={item} sectionType={sectionType} currentStepIndex={statusManualContents.progressStepIndex} />
      )}
      {statusManualContents && (
        <div className={'flex flex-col px-6 py-6'}>
          {statusManualContents.iconFileName?.endsWith('svg') && (
            <img src={`/images/illus-icons/${statusManualContents.iconFileName}`} alt="icon" className={'mx-auto mb-3 h-20 w-20'} />
          )}
          {statusManualContents.iconFileName?.endsWith('json') && (
            <div className={'mx-auto mb-3 h-20 w-20'}>
              <LottiePlayer src={`/lotties/summary/${statusManualContents.iconFileName}`} />
            </div>
          )}
          <div className={'text-17 mb-2 text-center font-semibold text-gray-900 whitespace-pre-line'}>{statusManualContents.title}</div>
          <div className={'text-13 whitespace-pre text-center text-gray-700'}>{statusManualContents.description}</div>
          {(statusManualContents.primaryButtonLabel || statusManualContents.basicButtonLabel) && (
            <div className={'mt-6 flex gap-2.5'}>
              {statusManualContents.basicButtonLabel && (
                <Button.Box state={'basic'} size={'default'} className={'py-3.5'} fullWidth={true} onClick={statusManualContents.basicButtonAction}>
                  {statusManualContents.basicButtonLabel}
                </Button.Box>
              )}
              {statusManualContents.primaryButtonLabel && (
                <Button.Box state={'primary'} size={'default'} className={'py-3.5'} fullWidth={true} onClick={statusManualContents.primaryButtonAction}>
                  {statusManualContents.primaryButtonLabel}
                </Button.Box>
              )}
            </div>
          )}
          {statusManualContents.textButtonLabel && (
            <div className={'mt-4 text-center'}>
              <Button.Text icon="arrow-right" size={'small'} onClick={statusManualContents.textButtonAction}>
                {statusManualContents.textButtonLabel}
              </Button.Text>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const ProgressStepper = ({ item, sectionType, currentStepIndex }: Pick<CurrentItemProps, 'item' | 'sectionType'> & { currentStepIndex: number }) => {
  const isCorps = sectionType.startsWith('corps');
  const convertResultDate = () => {
    if (item.refundResultDt) return format(item.refundResultDt, 'M월 d일');
    if (!item.refundResultExpectDt) return undefined;

    const expectedDate = new Date(item.refundResultExpectDt).getDate();
    const dateFormat = expectedDate > 20 ? 'M월 말' : expectedDate > 10 ? 'M월 중순' : 'M월 초';

    return format(item.refundResultExpectDt, dateFormat);
  };

  const step = (() => {
    const totalStep = [
      { title: '검토중', subText: currentStepIndex === 0 ? '3~4일 소요' : undefined },
      {
        title: '신고완료',
        subText: currentStepIndex > 0 && item.refundReportDt ? format(item.refundReportDt, 'M월 d일') : undefined
      },
      {
        title: '환급결정',
        subText: currentStepIndex > 0 ? convertResultDate() : undefined
      },
      {
        title: '환급완료',
        subText: currentStepIndex === 0 ? '2개월 소요' : isCorps ? convertResultDate() : undefined
      },
      { title: '이용료결제' }
    ];

    if (isCorps) {
      totalStep.splice(2, 1);
    }

    return totalStep;
  })();

  const stepCount = step.length;
  return (
    <div className={'relative mx-2.5 pb-2 pt-4'}>
      <div className={'absolute mt-[29px] flex w-full'} style={{ padding: `0 ${100 / stepCount / 2}%` }}>
        {Array.from({ length: step.length - 1 }).map((_, index) => (
          <div
            key={index}
            className={cn('h-0.5 bg-gray-300', { ['bg-violet-600']: index < currentStepIndex })}
            style={{ width: `${100 / (stepCount - 1)}%` }}
          />
        ))}
      </div>
      <div className={'flex'}>
        {step.map((item, index) => (
          <div key={index} className={'flex flex-col items-center'} style={{ width: `${100 / stepCount}%` }}>
            <div
              className={cn('text-12 rounded-lg px-1.5 font-medium leading-normal text-gray-500', {
                ['text-violet-600']: index < currentStepIndex,
                ['bg-violet-600 !text-white']: index === currentStepIndex
              })}
            >
              {item.title}
            </div>
            <div
              className={cn('z-[1] my-1.5 h-3 w-3 rounded-full border-2 border-gray-300 bg-white', {
                ['border-violet-600']: index <= currentStepIndex,
                ['shadow-[0_0_0_3px_rgba(94,80,250,0.2)]']: index === currentStepIndex
              })}
            />
            {item.subText && <div className={'text-[10px] font-medium text-gray-500'}>{item.subText}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};
